import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { logEventSafe } from '../analytics/analytics';
import { PageBackLink, PageHero, PagePanel, PageShell } from './MarketingPageShell';

const TeamInfoPage = () => {
  const location = useLocation();

  useEffect(() => {
    logEventSafe('page_view', {
      page_path: location.pathname,
      page_title: 'Team Info',
    });
  }, [location.pathname]);

  const handleBackClick = () => {
    logEventSafe('click_back', {
      page_path: location.pathname,
    });
  };

  return (
    <PageShell>
      <div className="space-y-10 py-4">
        <PageBackLink to="/pricing" onClick={handleBackClick}>
          Back to pricing
        </PageBackLink>

        <div className="mx-auto max-w-4xl space-y-8">
          <PageHero
            eyebrow="Teams"
            title="How Teams Work"
            description="Everything you need to know about setting up, inviting members, managing seats, and handling billing for your team."
          />

          <PagePanel className="border-white/10 bg-white/[0.045]">
            <div className="space-y-8">
              <section>
                <h2 className="mb-2 text-2xl font-semibold text-white">Overview</h2>
                <p className="leading-7 text-slate-300">
                  The Teams plan provides all team members Plus features at <strong>$6.50 per user per month</strong>. It is designed
                  for organizations and teams that need to manage multiple users under a single subscription.
                </p>
              </section>

              <section>
                <h2 className="mb-2 text-2xl font-semibold text-white">Manager &amp; Member Roles</h2>
                <p className="leading-7 text-slate-300">
                  The user who owns an organization account becomes a <strong>Manager</strong>. Managers have full access to billing,
                  seat allocation, and team settings. They can invite or remove Members. <strong>Members</strong> have access to
                  premium features but cannot change billing or subscription settings.
                </p>
              </section>

              <section>
                <h2 className="mb-2 text-2xl font-semibold text-white">Inviting Team Members</h2>
                <p className="leading-7 text-slate-300">
                  Navigate to your Team Dashboard in the Account page. Copy the <em>Invite Key</em>, distribute the key to your team members,
                  and they can join from their personal accounts. You can resend or revoke invitations at any time from the dashboard.
                </p>
              </section>

              <section>
                <h2 className="mb-2 text-2xl font-semibold text-white">Seat Management &amp; Billing</h2>
                <p className="leading-7 text-slate-300">
                  Each team member occupies a seat, and billing is based on the total number of active seats. When you add or
                  remove members, your seat count will automatically update, and the cost will adjust on your next billing cycle.
                  This creates a fair pricing plan that fits the size of your team.
                </p>
              </section>

              <section>
                <h2 className="mb-2 text-2xl font-semibold text-white">Managing Your Subscription</h2>
                <p className="leading-7 text-slate-300">
                  To change your payment method or cancel your Teams subscription, go to the <em>Subscription</em> section of your
                  Account page and click <em>Manage Subscription</em>. You&apos;ll be redirected to the Customer Portal for secure billing management.
                </p>
              </section>
            </div>
          </PagePanel>

          <div className="rounded-2xl bg-gray-800/50 p-8 shadow-lg ring-1 ring-white/10 md:p-10">
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div className="text-center md:text-left">
                <div className="text-sm uppercase tracking-[0.25em] text-blue-300">Ready to set up your team?</div>
                <h2 className="mt-2 text-2xl font-semibold leading-tight text-white">Choose the Teams plan when you&apos;re ready.</h2>
                <p className="mt-2 text-gray-400">
                  Head back to pricing to start with centralized billing and managed seats.
                </p>
              </div>

              <div className="flex w-full shrink-0 flex-col gap-3 md:w-auto md:min-w-[15rem]">
                <Link
                  to="/pricing"
                  className="inline-flex w-full items-center justify-center rounded-xl bg-blue-600 px-5 py-3 text-sm text-white transition-colors hover:bg-blue-700"
                >
                  View pricing
                </Link>
                <Link
                  to="/LoginPage?mode=signup&accountType=team"
                  className="inline-flex w-full items-center justify-center rounded-xl border border-white/10 bg-white/5 px-5 py-3 text-sm text-white transition-colors hover:bg-white/10"
                >
                  Start business account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default TeamInfoPage;
